import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import AddressInputWithAutocomplete from '../../../common/adress-input-with-autocomplete/address-input-with-autocomplete';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import ChecklistIcon from '@mui/icons-material/Checklist';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { ShopLocationType } from '../../../../interfaces/delivery-company/pickups';
import DownloadIcon from '@mui/icons-material/Download';
import React from 'react';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import { emptyPickupTask } from '../../../../data/pickuptasks';
import { Workflow } from '../../../../enums/orders';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { emptyCustomer } from '../../../../data/customers';
import { CustomerType } from '../../../../interfaces/common/customer';
import GenericTable from '../../../common/generic-table/generic-table';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import { useImmer } from 'use-immer';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { buildQueryParams } from '../../../common/filter-bar/filter-bar.utils';
import { showDeliveryState } from '../../../common/styles/order-states.style';
import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import {
  displayLocation,
  displayMultipleLocations,
} from '../../../../utils/locations';
import {
  canChangePickupTaskDriver,
  canCompletePickupTask,
} from '../../../../utils/pickup-tasks';
import { isMobile, isTablet } from 'react-device-detect';
import MoneyInput from '../../../common/money-input/money-input';
import { add, displayMoney } from '../../../../utils/money';
import { chipPadding } from '../../../common/styles/common';
import { useSnackBar } from '../../../../contexts/snackbar';
import { PickupTaskState } from '../../../../enums/tasks';
import { showPickupTaskState } from '../../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../../common/styles/workflow-display';
import { compute_total_pop } from '../../../common/report-builder/utils';
import { DriverWithIcon } from '../../../common/styles/driver-with-icon';
import PickupTaskWaybillsDialog from '../../../common/pickup-task-waybills-dialog';

interface EditPickupTaskProps {
  taskID: string;
}

function EditPickupTask({ taskID }: EditPickupTaskProps) {
  const [taskData, setTaskData] = useState<IPickupTask>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [removedOrdersIDs, setRemovedOrdersIDs] = useState<string[]>([]);
  const [showWaybillsDialog, setShowWaybillsDialog] = useState<boolean>(false);
  const { showMessage } = useSnackBar();

  const { user, config } = useContext(AuthContext);

  useEffect(() => {
    const getAndSetTaskData = async () => {
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${taskID}/`;
      try {
        console.log(endpoint);
        const response = await axios.get<IPickupTask>(endpoint, config);
        console.log(response);
        setTaskData(response.data);
      } catch (error) {
        console.log(error);
        if (axios.isAxiosError(error)) {
          setResponse(error.response);
          setMessage(
            'Failed to get task details: ' + error.response?.statusText
          );
        } else {
          setMessage('Failed to get task details');
        }
      }
      setIsLoading(false);
    };

    getAndSetTaskData();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (taskData) {
      setIsLoading(true);
      setResponse(undefined);

      const requestData: any = {
        orders: taskData.orders
          .filter((order) => !removedOrdersIDs.includes(order.id))
          .map((order) => {
            return { order_id: order.order_id };
          }),
        pop: taskData.pop,
      };

      try {
        const response = await axios.patch(
          `${API_ENDPOINT}/api/v1/pickup-task/${taskData.id}/`,
          requestData,
          config
        );

        setResponse(response);
        setMessage('Pickup request updated successfully');
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage(
            'Failed to update pickup request: ' + e.response?.statusText
          );
        } else {
          setMessage('Failed to update pickup request');
        }
        console.log(e);
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const isFrozen = !(
    taskData &&
    [
      PickupTaskState.CREATED,
      PickupTaskState.ASSIGNED,
      PickupTaskState.IN_PROGRESS,
    ].includes(taskData.status)
  );

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 1 }}
      >
        Pickup Request #{taskID}
      </Typography>

      <div style={{ width: '100%', textAlign: 'center' }}>
        {taskData && <>{showPickupTaskState(taskData?.status)}</>}
        <br />
      </div>

      {isFrozen && (
        <>
          <Alert severity="info">
            {' '}
            This pickup request can not be modified anymore. Please create a new
            pickup request if needed.
          </Alert>{' '}
          <br />
        </>
      )}

      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message !== '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>
          )}

          {taskData && (
            <div style={{ width: '100%', textAlign: 'center' }}>
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <FormLabel id="demo-radio-buttons-group-label">
                    Workflow
                  </FormLabel>
                  <RadioGroup
                    sx={{ display: 'flex', justifyContent: 'center' }}
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue={taskData.workflow}
                    row
                    name="radio-buttons-group"
                    onChange={(e) => {
                      setTaskData({
                        ...taskData,
                        workflow: e.target.value as Workflow,
                      });
                    }}
                  >
                    <FormControlLabel
                      disabled
                      value={Workflow.STANDARD}
                      control={<Radio />}
                      label="Standard"
                    />
                    <FormControlLabel
                      disabled
                      value={Workflow.RETURN}
                      control={<Radio />}
                      label="Return"
                    />
                  </RadioGroup>
                </FormControl>

                {taskData.workflow === Workflow.RETURN && (
                  <>
                    <b>Pickup for:</b> {taskData.customer?.name}
                    <br /> <b>Pickup at:</b>{' '}
                    {displayLocation(taskData.customer_location)}
                    <br />
                    <br />
                  </>
                )}

                {taskData.driver && (
                  <DriverWithIcon centered driver={taskData.driver} showPhone />
                )}

                <Typography
                  variant="h5"
                  component="h5"
                  sx={{ textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Total Pay on Pickup:{' '}
                  {displayMoney(
                    add(
                      compute_total_pop(
                        taskData.orders.filter(
                          (order) => !removedOrdersIDs.includes(order.id)
                        )
                      ),
                      taskData.pop
                    )
                  )}
                </Typography>

                {
                  <>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 3,
                      }}
                    >
                      <Chip
                        icon={<DownloadIcon fontSize="large" color="primary" />}
                        onClick={() => {
                          setShowWaybillsDialog(true);
                        }}
                        style={{ padding: chipPadding }}
                        label={<b>Print Waybills</b>}
                      />
                    </div>
                    <br />
                    <br />
                  </>
                }

                <Chip
                  icon={<ChecklistIcon />}
                  color="primary"
                  label={`Task Orders: ${taskData.orders.length - removedOrdersIDs.length}`}
                  variant={'outlined'}
                />
                <br />

                <GenericTable
                  sourceView="merchant-edit-task-form"
                  items={taskData.orders}
                  fullWidth
                  rowStyles={(order) =>
                    removedOrdersIDs.includes(order.id)
                      ? { background: '#ccc', textDecoration: 'line-through' }
                      : {}
                  }
                  columns={[
                    {
                      id: 'order_id',
                      header: 'Order ID',
                      display_body: (order) => <>{order.order_id}</>,
                    },
                    {
                      id: 'packages',
                      header: '# Packages',
                      display_body: (order) => <>{order.number_of_packages}</>,
                    },
                    {
                      id: 'customer',
                      header: 'Customer',
                      display_body: (order) => (
                        <>
                          {order.customer?.name}
                          <br /> {displayLocation(order.customer_location)}
                        </>
                      ),
                    },
                    {
                      id: 'pop',
                      header: 'Pay on Pickup',
                      display_body: (order) => displayMoney(order.pop),
                    },
                    {
                      id: 'workflow',
                      header: 'Workflow',
                      display_body: (order) =>
                        showWorkflow(
                          order.workflow,
                          order.is_exchange,
                          order.exchange_for,
                          order.return_reason
                        ),
                    },
                    {
                      id: 'delivery state',
                      header: 'Status',
                      display_body: (order) =>
                        showDeliveryState(order.delivery_state),
                    },
                    {
                      id: 'remove_order',
                      header: 'Remove Order',
                      display_body: (order) => (
                        <Button
                          size={isMobile && !isTablet ? 'small' : 'medium'}
                          disabled={isFrozen}
                          variant="contained"
                          color={
                            removedOrdersIDs.includes(order.id)
                              ? 'primary'
                              : 'error'
                          }
                          onClick={() =>
                            removedOrdersIDs.includes(order.id)
                              ? setRemovedOrdersIDs(
                                  removedOrdersIDs.filter(
                                    (item) => item != order.id
                                  )
                                )
                              : setRemovedOrdersIDs([
                                  ...removedOrdersIDs,
                                  order.id,
                                ])
                          }
                        >
                          {removedOrdersIDs.includes(order.id)
                            ? 'Add Back'
                            : 'Remove'}
                        </Button>
                      ),
                    },
                  ]}
                  getItemUID={(order) => order.id}
                  selectedItems={new Map()}
                  isLoading={false}
                />

                <br />
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <Button
                    size={isMobile && !isTablet ? 'small' : 'medium'}
                    disabled={isFrozen}
                    variant="contained"
                    type={'submit'}
                  >
                    Save
                  </Button>
                </div>
              </form>
              {showWaybillsDialog && taskData && (
                <PickupTaskWaybillsDialog
                  pickupTaskId={taskData.id}
                  onClose={() => setShowWaybillsDialog(false)}
                />
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

export default EditPickupTask;
